
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, User, Lock, Mail } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

// Define profile type to match the database structure
type Profile = {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  updated_at?: string | null;
  created_at?: string | null;
};

const profileFormSchema = z.object({
  fullName: z.string().min(2, {
    message: "İsim en az 2 karakter olmalıdır.",
  }),
});

const passwordFormSchema = z.object({
  currentPassword: z.string().min(6, {
    message: "Şifre en az 6 karakter olmalıdır.",
  }),
  newPassword: z.string().min(6, {
    message: "Şifre en az 6 karakter olmalıdır.",
  }),
  confirmPassword: z.string().min(6, {
    message: "Şifre en az 6 karakter olmalıdır.",
  }),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Şifreler eşleşmiyor",
  path: ["confirmPassword"],
});

const Settings = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<Profile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const profileForm = useForm<z.infer<typeof profileFormSchema>>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      fullName: "",
    },
  });

  const passwordForm = useForm<z.infer<typeof passwordFormSchema>>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    // Kullanıcı değilse yönlendir
    if (!user && !isLoading) {
      navigate('/auth');
    }
    
    // Kullanıcı profil bilgilerini getir
    const fetchProfileData = async () => {
      if (user) {
        try {
          setIsProfileLoading(true);
          setError(null);
          
          // Add type assertion to the Supabase query
          const { data, error } = await supabase
            .from('profiles' as any)
            .select('*' as any)
            .eq('id', user.id)
            .single();
          
          if (error) throw error;
          
          if (data) {
            // Explicit type casting for TypeScript
            const typedData = data as unknown as Profile;
            setProfileData(typedData);
            profileForm.reset({ 
              fullName: typedData.full_name || "" 
            });
          }
        } catch (error: any) {
          console.error('Profil bilgileri alınamadı:', error.message);
          setError('Profil bilgileri alınamadı.');
        } finally {
          setIsProfileLoading(false);
        }
      }
    };
    
    if (user) {
      fetchProfileData();
    }
  }, [user, navigate, isLoading]);

  const onProfileSubmit = async (data: z.infer<typeof profileFormSchema>) => {
    if (!user) return;
    
    try {
      setIsProfileLoading(true);
      setError(null);
      
      const updateData = {
        full_name: data.fullName,
        updated_at: new Date().toISOString(),
      };
      
      // Add type assertion to the Supabase query
      const { error } = await supabase
        .from('profiles' as any)
        .update(updateData as any)
        .eq('id', user.id);
      
      if (error) throw error;
      
      toast({
        title: "Profil güncellendi",
        description: "Profil bilgileriniz başarıyla güncellendi.",
      });
      
      // Güncel bilgileri çek
      // Add type assertion to the Supabase query
      const { data: updatedData, error: fetchError } = await supabase
        .from('profiles' as any)
        .select('*' as any)
        .eq('id', user.id)
        .single();
      
      if (fetchError) throw fetchError;
      
      if (updatedData) {
        // Explicit type casting for TypeScript
        setProfileData(updatedData as unknown as Profile);
      }
      
    } catch (error: any) {
      console.error('Profil güncellenemedi:', error.message);
      setError('Profil güncellenemedi: ' + error.message);
      toast({
        title: "Hata",
        description: "Profil güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    } finally {
      setIsProfileLoading(false);
    }
  };

  const onPasswordSubmit = async (data: z.infer<typeof passwordFormSchema>) => {
    if (!user) return;
    
    try {
      setIsProfileLoading(true);
      setError(null);
      
      // Şifre değiştir
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword,
      });
      
      if (error) throw error;
      
      toast({
        title: "Şifre güncellendi",
        description: "Şifreniz başarıyla güncellendi.",
      });
      
      // Formu temizle
      passwordForm.reset();
      
    } catch (error: any) {
      console.error('Şifre güncellenemedi:', error.message);
      setError('Şifre güncellenemedi: ' + error.message);
      toast({
        title: "Hata",
        description: "Şifre güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    } finally {
      setIsProfileLoading(false);
    }
  };

  if (isLoading || !user) {
    return <div className="flex items-center justify-center min-h-screen">Yükleniyor...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Hesap Ayarları</h1>
      
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 md:w-auto md:inline-flex">
          <TabsTrigger value="profile" className="flex items-center">
            <User className="mr-2 h-4 w-4" />
            <span>Profil</span>
          </TabsTrigger>
          <TabsTrigger value="password" className="flex items-center">
            <Lock className="mr-2 h-4 w-4" />
            <span>Şifre</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profil Bilgileri</CardTitle>
              <CardDescription>
                Kişisel bilgilerinizi güncelleyebilirsiniz. Bu bilgiler başka kullanıcılar tarafından görüntülenemez.
              </CardDescription>
            </CardHeader>
            <Form {...profileForm}>
              <form onSubmit={profileForm.handleSubmit(onProfileSubmit)}>
                <CardContent className="space-y-4">
                  <FormField
                    control={profileForm.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ad Soyad</FormLabel>
                        <FormControl>
                          <Input placeholder="Ad Soyad" {...field} />
                        </FormControl>
                        <FormDescription>
                          Mağazanız ve profilinizde görünecek isim.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">E-posta</Label>
                    <div className="flex items-center">
                      <Mail className="mr-2 h-4 w-4 text-gray-500" />
                      <Input 
                        id="email" 
                        type="email" 
                        value={user.email || ''} 
                        disabled 
                        className="bg-gray-100" 
                      />
                    </div>
                    <p className="text-sm text-gray-500">
                      E-posta adresinizi değiştirmek için lütfen destek ekibiyle iletişime geçin.
                    </p>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    type="submit" 
                    disabled={isProfileLoading || !profileForm.formState.isDirty}
                  >
                    {isProfileLoading ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </Card>
        </TabsContent>
        
        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle>Şifre Değiştir</CardTitle>
              <CardDescription>
                Hesabınızın güvenliği için şifrenizi düzenli olarak değiştirin. Güçlü ve benzersiz şifreler kullanın.
              </CardDescription>
            </CardHeader>
            <Form {...passwordForm}>
              <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}>
                <CardContent className="space-y-4">
                  <FormField
                    control={passwordForm.control}
                    name="currentPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mevcut Şifre</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="********" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={passwordForm.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Yeni Şifre</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="********" {...field} />
                        </FormControl>
                        <FormDescription>
                          En az 6 karakter uzunluğunda olmalıdır.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={passwordForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Şifreyi Onayla</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="********" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
                <CardFooter>
                  <Button 
                    type="submit" 
                    disabled={isProfileLoading || !passwordForm.formState.isDirty}
                  >
                    {isProfileLoading ? 'Kaydediliyor...' : 'Şifreyi Değiştir'}
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
